import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
	AdvertiserOwnerOptionsQueryUsers, RedditAdvertiserDocument, RedditAdvertiserQueryUserUser,
} from 'wb-reddit/graphql/generated';
import useRedditAdvertiserMutator from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserMutator';
import { AuthGraphAdvertiserDocument } from 'wb-spotify/graphql/authgraph/generated';
import { handleErrorNotification, isReddit } from '@aleph/shared-components';
import { captureException } from '@sentry/react';
import { useTranslation } from 'react-i18next';

/**
 * Reason for this function is to make sure the type returned from fetchAdvertiser
 * query matches the type of agencyAccountOwner. This way if BE changes the structure or fields names or w/e
 * just adjust the types in this function
 */
function preprocessAgencyAccountOwner(user?: RedditAdvertiserQueryUserUser) {
	if (!user) {
		return null;
	}

	return {
		__typename:            'SearchUser',
		userId:                user.userId,
		userFullName:          user.userFullName,
		userEmail:             user.userEmail,
		userProfilePictureUrl: user.userProfilePictureUrl,
		id:                    user.id,
		userAccountLevel:      user.userAccountLevel,
		profilePicture:        user.profilePicture,
	} satisfies AdvertiserOwnerOptionsQueryUsers;
}

const useRedditAdvertiserUrlOverlay = () => {
	const navigate = useNavigate();
	const { params: editAdvertiserId } = useParams();
	const { t } = useTranslation();

	const { setMutatingAdvertiser, setLoading } = useRedditAdvertiserMutator();
	const [fetchAdvertiser] = useLazyQuery(isReddit() ? RedditAdvertiserDocument : AuthGraphAdvertiserDocument);

	const setEditingAdvertiser = async (advertiserId: string) => {
		setLoading(true);
		const { data, error } = await fetchAdvertiser({ variables: { advertiserId } });
		const path = isReddit() ? 'agencyAccounts' : 'advertiserAccounts';
		const advertiser = data?.[path]?.items?.[0];

		if (error || !advertiser) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: something went wrong while fetching advertiser',
					'Something went wrong while fetching advertiser',
				),
			);

			navigate('/advertisers');

			return;
		}

		setMutatingAdvertiser({
			...advertiser,
			agencyAccountOwner:
				isReddit()
					? preprocessAgencyAccountOwner(
						advertiser?.users
							?.find(user => user.agencyIsOwner)?.user,
					) : advertiser.owner,
		});

		setLoading(false);
	};

	useEffect(() => {
		if (editAdvertiserId) {
			setEditingAdvertiser(editAdvertiserId);
		} else {
			setMutatingAdvertiser(undefined);
		}
	}, [editAdvertiserId]);
};

export default useRedditAdvertiserUrlOverlay;
